import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { AlertTriangle, ArrowDown, ArrowUp } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { StringMultiSelect } from '@/components/StringMultiSelect';
import { useIntuneDevices, type IntuneDevice } from '@/api/cipp';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cippTenantPath, rmmDevicePath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

/**
 * CIPP → Intune devices, across every tenant, cross-linked to Datto RMM by serial
 * number (hostname fallback within the same client — see server/cipp/devices.ts).
 * The headline use: Windows/macOS machines enrolled in Intune with no RMM agent.
 */

type RmmFilter = 'any' | 'linked' | 'missing';
type ComplianceFilter = 'any' | 'compliant' | 'noncompliant' | 'other';
type SortKey = 'device' | 'client' | 'lastSync';

interface Filters {
  search: string;
  clients: string[];
  os: string[];
  compliance: ComplianceFilter;
  rmm: RmmFilter;
  staleOnly: boolean;
}

const DEFAULT_FILTERS: Filters = { search: '', clients: [], os: [], compliance: 'any', rmm: 'any', staleOnly: false };
const STORAGE_KEY = 'cosmos:cipp:devices';
const STALE_DAYS = 30;

/** Only computers can carry the Datto RMM agent — phones/tablets are never "missing" from RMM. */
const rmmEligible = (d: IntuneDevice) => d.os === 'Windows' || d.os === 'macOS';
const clientName = (d: IntuneDevice) => d.companyName ?? d.tenant.displayName;
const isStale = (d: IntuneDevice) => !d.lastSync || differenceInCalendarDays(new Date(), new Date(d.lastSync)) > STALE_DAYS;
const complianceBucket = (c: string | null): Exclude<ComplianceFilter, 'any'> =>
  c === 'compliant' ? 'compliant' : c === 'noncompliant' ? 'noncompliant' : 'other';

function load<T>(key: string, fallback: T): T {
  try { const r = localStorage.getItem(key); return r ? { ...fallback, ...JSON.parse(r) } as T : fallback; } catch { return fallback; }
}
function save(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

/** Pre-filtered views from the sidebar / other pages (`cippDevicesView`). */
function filtersFromSearch(sp: URLSearchParams, items: IntuneDevice[]): Partial<Filters> | null {
  const f: Partial<Filters> = {};
  const rmm = sp.get('rmm');
  if (rmm === 'missing' || rmm === 'linked') f.rmm = rmm;
  const compliance = sp.get('compliance');
  if (compliance === 'compliant' || compliance === 'noncompliant' || compliance === 'other') f.compliance = compliance;
  const tenant = sp.get('tenant');
  if (tenant) {
    const d = items.find((x) => x.tenant.customerId === tenant);
    if (d) f.clients = [clientName(d)];
  }
  if (sp.get('q')) f.search = sp.get('q')!;
  return Object.keys(f).length ? f : null;
}

export default function CippDevicesPage() {
  const devices = useIntuneDevices({ scoped: true });
  // While a client is picked in the header, this page's own Client filter is ignored (and hidden).
  const { companyId: scopeId } = useScopeFilter();
  const items = useMemo(() => devices.data?.items ?? [], [devices.data]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => load(STORAGE_KEY, DEFAULT_FILTERS));
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'device', dir: 'asc' });

  // A view in the URL replaces the saved filters, then leaves the URL. Waits for data when it needs
  // it (a ?tenant= view resolves to a client name).
  useEffect(() => {
    if (![...searchParams.keys()].length) return;
    if (searchParams.get('tenant') && items.length === 0) return;
    const view = filtersFromSearch(searchParams, items);
    if (view) setFilters({ ...DEFAULT_FILTERS, ...view });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, items]);

  useEffect(() => save(STORAGE_KEY, filters), [filters]);
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setFilters((f) => ({ ...f, [k]: v }));

  const clientOpts = useMemo(
    () => [...new Set(items.map(clientName))].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v })),
    [items],
  );
  const osOpts = useMemo(
    () => [...new Set(items.map((d) => d.os ?? 'Unknown'))].sort().map((v) => ({ value: v, label: v })),
    [items],
  );

  const clients = scopeId != null ? [] : filters.clients;
  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const out = items.filter((d) =>
      (!q || [d.deviceName, d.serialNumber, d.userPrincipalName, d.userDisplayName, d.model, d.rmm?.hostname]
        .some((s) => s?.toLowerCase().includes(q)))
      && (clients.length === 0 || clients.includes(clientName(d)))
      && (filters.os.length === 0 || filters.os.includes(d.os ?? 'Unknown'))
      && (filters.compliance === 'any' || complianceBucket(d.compliance) === filters.compliance)
      && (filters.rmm === 'any' || (filters.rmm === 'linked' ? !!d.rmm : rmmEligible(d) && !d.rmm))
      && (!filters.staleOnly || isStale(d)));
    const dir = sort.dir === 'asc' ? 1 : -1;
    const key = (d: IntuneDevice) =>
      sort.key === 'client' ? clientName(d).toLowerCase()
      : sort.key === 'lastSync' ? d.lastSync ?? ''
      : d.deviceName.toLowerCase();
    return out.sort((a, b) => (key(a) < key(b) ? -dir : key(a) > key(b) ? dir : 0));
  }, [items, filters, sort, clients]);

  // Tiles count across the current client/OS scope so they stay meaningful when narrowed to one client.
  const scoped = useMemo(() => items.filter((d) =>
    (clients.length === 0 || clients.includes(clientName(d)))
    && (filters.os.length === 0 || filters.os.includes(d.os ?? 'Unknown'))), [items, clients, filters.os]);
  const tiles = [
    { label: 'Devices', value: scoped.length, active: filters.rmm === 'any' && filters.compliance === 'any' && !filters.staleOnly,
      onClick: () => setFilters((f) => ({ ...f, rmm: 'any', compliance: 'any', staleOnly: false })) },
    { label: 'Not in Datto RMM', hint: 'Windows & macOS', value: scoped.filter((d) => rmmEligible(d) && !d.rmm).length, tone: 'warn' as const,
      active: filters.rmm === 'missing', onClick: () => setFilters((f) => ({ ...f, rmm: 'missing', compliance: 'any', staleOnly: false })) },
    { label: 'Non-compliant', value: scoped.filter((d) => d.compliance === 'noncompliant').length, tone: 'bad' as const,
      active: filters.compliance === 'noncompliant', onClick: () => setFilters((f) => ({ ...f, compliance: 'noncompliant', rmm: 'any', staleOnly: false })) },
    { label: `No sync ${STALE_DAYS}+ days`, value: scoped.filter(isStale).length,
      active: filters.staleOnly, onClick: () => setFilters((f) => ({ ...f, staleOnly: true, rmm: 'any', compliance: 'any' })) },
  ];

  const isDefault = JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS);
  const tenantsInfo = devices.data?.tenants;

  return (
    <DesktopOnly title="CIPP is available on desktop" message="Open Cosmos on a larger screen to browse Intune devices.">
      <div className="mx-auto w-full max-w-[1900px] px-4 py-4">
        <PageHeader
          title="Intune devices"
          count={devices.data ? filtered.length : null}
          isFetching={devices.isFetching}
          subtitle={<>
            CIPP · Intune, matched to Datto RMM by serial number
            {devices.data && <> · Updated {fmtRelative(devices.data.fetchedAt)}</>}
            {tenantsInfo && <> · {tenantsInfo.total - tenantsInfo.unlicensed} tenants with Intune</>}
          </>}
        />

        {devices.isLoading && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Loading Intune devices from every tenant — the first load can take up to half a minute…
          </div>
        )}
        {devices.error && (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Failed to load devices: {devices.error instanceof Error ? devices.error.message : 'Unknown error'}
          </div>
        )}

        {devices.data && (
          <>
            <div className="mt-3 grid grid-cols-4 gap-3">
              {tiles.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={t.onClick}
                  className={cn(
                    'rounded-lg border bg-card px-4 py-3 text-left shadow-sm transition-colors hover:bg-muted/40',
                    t.active ? 'border-primary ring-1 ring-primary/40' : 'border-border',
                  )}
                >
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {t.label}{'hint' in t && t.hint && <span className="ml-1 normal-case tracking-normal">· {t.hint}</span>}
                  </div>
                  <div className={cn(
                    'mt-1 text-2xl font-semibold tabular-nums',
                    t.value > 0 && 'tone' in t && t.tone === 'bad' && 'text-destructive',
                    t.value > 0 && 'tone' in t && t.tone === 'warn' && 'text-warning',
                  )}>
                    {t.value}
                  </div>
                </button>
              ))}
            </div>

            <div className="sticky top-0 z-10 -mx-4 mt-2 border-b bg-background/80 px-4 py-3 backdrop-blur">
              <div className="flex flex-wrap items-center gap-2">
                <SearchInput withIcon placeholder="Device, serial, user, model…" className="w-64" value={filters.search} onValueChange={(v) => set('search', v)} />
                {scopeId == null && <StringMultiSelect label="Client" options={clientOpts} selected={filters.clients} onChange={(v) => set('clients', v)} />}
                <StringMultiSelect label="OS" options={osOpts} selected={filters.os} onChange={(v) => set('os', v)} />
                <select
                  value={filters.compliance}
                  onChange={(e) => set('compliance', e.target.value as ComplianceFilter)}
                  className={cn('h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground', filters.compliance !== 'any' && 'bg-secondary')}
                >
                  <option value="any">Any compliance</option>
                  <option value="compliant">Compliant</option>
                  <option value="noncompliant">Non-compliant</option>
                  <option value="other">Grace period / unknown</option>
                </select>
                <select
                  value={filters.rmm}
                  onChange={(e) => set('rmm', e.target.value as RmmFilter)}
                  className={cn('h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground', filters.rmm !== 'any' && 'bg-secondary')}
                >
                  <option value="any">Any RMM status</option>
                  <option value="linked">In Datto RMM</option>
                  <option value="missing">Not in Datto RMM (computers)</option>
                </select>
                <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <input type="checkbox" checked={filters.staleOnly} onChange={(e) => set('staleOnly', e.target.checked)} />
                  No sync {STALE_DAYS}+ days
                </label>
                {!isDefault && (
                  <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
                    Reset
                  </button>
                )}
              </div>
            </div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
              <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 1120 }}>
                <colgroup>
                  <col style={{ width: 240 }} /><col style={{ width: 170 }} /><col style={{ width: 200 }} /><col style={{ width: 120 }} />
                  <col style={{ width: 120 }} /><col style={{ width: 140 }} /><col />
                </colgroup>
                <thead className="bg-muted/50">
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <SortTh label="Device" k="device" sort={sort} setSort={setSort} />
                    <SortTh label="Client" k="client" sort={sort} setSort={setSort} />
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">OS</th>
                    <th className="px-3 py-2 font-medium">Compliance</th>
                    <SortTh label="Last sync" k="lastSync" sort={sort} setSort={setSort} />
                    <th className="px-3 py-2 font-medium">Datto RMM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {filtered.map((d) => <DeviceRow key={`${d.tenant.customerId}:${d.id}`} d={d} />)}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">No devices match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DesktopOnly>
  );
}

function SortTh({ label, k, sort, setSort }: {
  label: string; k: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  setSort: (s: { key: SortKey; dir: 'asc' | 'desc' }) => void;
}) {
  const active = sort.key === k;
  const Icon = active && sort.dir === 'desc' ? ArrowDown : ArrowUp;
  return (
    <th className="whitespace-nowrap px-3 py-2 font-medium">
      <button
        type="button"
        onClick={() => setSort({ key: k, dir: active && sort.dir === 'asc' ? 'desc' : k === 'lastSync' && !active ? 'desc' : 'asc' })}
        className={cn('inline-flex items-center gap-1 uppercase tracking-wider transition-colors', active ? 'text-foreground' : 'hover:text-foreground')}
      >
        {label}<Icon className={cn('h-3 w-3', active ? 'opacity-80' : 'opacity-40')} />
      </button>
    </th>
  );
}

const COMPLIANCE: Record<string, { label: string; cls: string }> = {
  compliant: { label: 'Compliant', cls: 'bg-success-soft text-success' },
  noncompliant: { label: 'Non-compliant', cls: 'bg-destructive/10 text-destructive' },
  inGracePeriod: { label: 'Grace period', cls: 'bg-warning-soft text-warning' },
};

function DeviceRow({ d }: { d: IntuneDevice }) {
  const comp = (d.compliance ? COMPLIANCE[d.compliance] : undefined) ?? { label: d.compliance ?? 'Unknown', cls: 'bg-muted text-muted-foreground' };
  const stale = isStale(d);
  return (
    <tr className="align-top hover:bg-muted/30">
      <td className="overflow-hidden px-3 py-2">
        <div className="truncate font-medium" title={d.deviceName}>{d.deviceName}</div>
        <div className="truncate text-xs text-muted-foreground" title={[d.manufacturer, d.model, d.serialNumber].filter(Boolean).join(' ')}>
          {[d.manufacturer, d.model].filter(Boolean).join(' ')}
          {d.serialNumber && <span className="ml-1.5 font-mono text-[11px]">{d.serialNumber}</span>}
        </div>
      </td>
      <td className="overflow-hidden px-3 py-2">
        <Link to={cippTenantPath(d.tenant.customerId)} className="block truncate hover:underline" title={clientName(d)}>{clientName(d)}</Link>
        {d.ownership === 'personal' && <div className="text-[11px] text-muted-foreground">Personal device</div>}
      </td>
      <td className="overflow-hidden px-3 py-2">
        {d.userDisplayName || d.userPrincipalName
          ? <>
              <div className="truncate" title={d.userDisplayName ?? d.userPrincipalName ?? ''}>{d.userDisplayName ?? d.userPrincipalName}</div>
              {d.userDisplayName && d.userPrincipalName && <div className="truncate text-xs text-muted-foreground" title={d.userPrincipalName}>{d.userPrincipalName}</div>}
            </>
          : <span className="text-xs text-muted-foreground">—</span>}
      </td>
      <td className="overflow-hidden px-3 py-2 text-xs">
        <div className="truncate">{d.os ?? '—'}</div>
        {d.osVersion && <div className="truncate font-mono text-[11px] text-muted-foreground" title={d.osVersion}>{d.osVersion}</div>}
      </td>
      <td className="px-3 py-2">
        <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium', comp.cls)}>{comp.label}</span>
        {d.encrypted === false && rmmEligible(d) && <div className="mt-0.5 text-[11px] text-warning">Not encrypted</div>}
      </td>
      <td className={cn('whitespace-nowrap px-3 py-2 text-xs', stale ? 'text-warning' : 'text-muted-foreground')}>
        {d.lastSync ? <span title={new Date(d.lastSync).toLocaleString()}>{fmtRelative(d.lastSync)}</span> : 'Never'}
      </td>
      <td className="overflow-hidden px-3 py-2 text-xs">
        {d.rmm ? (
          <Link to={rmmDevicePath(d.rmm.uid)} className="group inline-flex max-w-full flex-col">
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground group-hover:underline">
              <span className={cn('h-1.5 w-1.5 rounded-full', d.rmm.online ? 'bg-success' : 'bg-muted-foreground/50')} />
              {d.rmm.hostname}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {d.rmm.online ? 'Online' : d.rmm.lastSeen ? `Seen ${fmtRelative(d.rmm.lastSeen)}` : 'Offline'}
              {d.rmm.matchedBy === 'hostname' && ' · matched by name'}{d.rmm.matchedBy === 'mac' && ' · matched by MAC'}
            </span>
            {d.rmm.companyMismatch && (
              <span className="inline-flex items-center gap-1 text-[11px] text-warning"><AlertTriangle className="h-3 w-3" /> Different client in RMM</span>
            )}
          </Link>
        ) : rmmEligible(d) ? (
          <span className="font-medium text-warning">Not in Datto RMM</span>
        ) : (
          <span className="text-muted-foreground">n/a</span>
        )}
      </td>
    </tr>
  );
}
